/**
 * An organization's library of MCP servers, skills and plugins, and whether
 * it is the only thing its members' sessions may use (§5.26, §5.25) —
 * `policy.extensions` in its settings.
 *
 *   library    the approved entries, in the shapes a workspace's
 *              `agentExtensions` already takes (`settings/extensions.ts`) — a
 *              member switches one on for a workspace by adding it there
 *   allowlist  on, a session whose agent would be given an MCP server, a skill
 *              or a plugin that is not in the library is refused before it
 *              starts, in the main process, naming the organization
 *
 * An MCP server's environment holds no credential (`mcpProblem` refuses one, as
 * for a workspace): a server that needs a token names the **organization
 * secrets** it reads (`secrets`), which reach a session of the organization's
 * workspaces as environment variables like every organization secret — the
 * library holds their names, never their values. A tracker's shared sign-in is
 * not one of them: it never reaches a session.
 *
 * Policy rather than a held default: a member's own sessions on every
 * workspace are what it is about — the person is the member — and an allowlist
 * a member could switch off is no allowlist.
 */
import { ORG_SECRET_NAME } from '../cloud/org-secrets';
import { isIntegrationSecret } from '../cloud/shared-integrations';
import {
  MAX_MCP_SERVERS,
  MAX_PLUGINS,
  MAX_SKILLS,
  mcpProblem,
  normalizeExtensions,
  pluginProblem,
  skillProblem,
  type AgentExtensions,
  type McpServerEntry,
  type PluginEntry,
  type SkillEntry
} from '../settings/extensions';
import { isPlainObject, sameValue } from '../workspace/shape';

export type LibraryMcpServer = Omit<McpServerEntry, 'enabled'> & {
  /** The organization secrets it reads from the session's environment — names only. */
  secrets: string[];
};
export type LibrarySkill = Omit<SkillEntry, 'enabled'>;
export type LibraryPlugin = Omit<PluginEntry, 'enabled'>;

export interface OrgExtensions {
  library: { mcpServers: LibraryMcpServer[]; skills: LibrarySkill[]; plugins: LibraryPlugin[] };
  allowlist: boolean;
}

export const EMPTY_ORG_EXTENSIONS: OrgExtensions = { library: { mcpServers: [], skills: [], plugins: [] }, allowlist: false };

const MAX_SECRETS_PER_SERVER = 12;

/** The names an entry may read, as a document carries them. */
function secretNames(value: unknown): string[] {
  return Array.isArray(value)
    ? [...new Set(value.filter((one): one is string => typeof one === 'string' && ORG_SECRET_NAME.test(one) && !isIntegrationSecret(one)))].slice(
        0,
        MAX_SECRETS_PER_SERVER
      )
    : [];
}

/** The library as a document carries it, what does not read dropped — what a machine reads. */
export function orgExtensionsOf(raw: unknown): OrgExtensions {
  if (!isPlainObject(raw)) return EMPTY_ORG_EXTENSIONS;
  const library = isPlainObject(raw.library) ? raw.library : {};
  const normal = normalizeExtensions(library);
  const servers = Array.isArray(library.mcpServers) ? library.mcpServers : [];
  return {
    library: {
      mcpServers: normal.mcpServers.map(({ enabled: _on, ...entry }) => ({
        ...entry,
        secrets: secretNames((servers.find((one) => isPlainObject(one) && one.name === entry.name) as { secrets?: unknown } | undefined)?.secrets)
      })),
      skills: normal.skills.map(({ enabled: _on, ...entry }) => entry),
      plugins: normal.plugins.map(({ enabled: _on, ...entry }) => entry)
    },
    allowlist: raw.allowlist === true
  };
}

/** The library as an administrator wrote it — each refusal a sentence with its path, nothing dropped silently. */
export function parseOrgExtensions(input: unknown, problems: string[]): OrgExtensions {
  if (input === undefined || input === null) return EMPTY_ORG_EXTENSIONS;
  const at = 'policy.extensions';
  if (!isPlainObject(input)) {
    problems.push(`${at}: Expected an object.`);
    return EMPTY_ORG_EXTENSIONS;
  }
  for (const key of Object.keys(input)) if (key !== 'library' && key !== 'allowlist') problems.push(`${at}.${key}: Not a field this version of Airlock knows.`);
  if (input.allowlist !== undefined && typeof input.allowlist !== 'boolean') problems.push(`${at}.allowlist: Expected true or false.`);
  const library = input.library ?? {};
  if (!isPlainObject(library)) {
    problems.push(`${at}.library: Expected an object.`);
    return EMPTY_ORG_EXTENSIONS;
  }
  const lists = [
    ['mcpServers', MAX_MCP_SERVERS, (entry: Record<string, unknown>) => mcpProblem({ ...entry, enabled: true } as Partial<McpServerEntry>)],
    ['skills', MAX_SKILLS, (entry: Record<string, unknown>) => skillProblem(entry as Partial<SkillEntry>)],
    ['plugins', MAX_PLUGINS, (entry: Record<string, unknown>) => pluginProblem(entry as Partial<PluginEntry>)]
  ] as const;
  for (const key of Object.keys(library)) if (!lists.some(([name]) => name === key)) problems.push(`${at}.library.${key}: Not a field this version of Airlock knows.`);
  for (const [key, max, problemOf] of lists) {
    const list = library[key];
    if (list === undefined || list === null) continue;
    if (!Array.isArray(list)) {
      problems.push(`${at}.library.${key}: Expected a list.`);
      continue;
    }
    if (list.length > max) problems.push(`${at}.library.${key}: ${max} at most.`);
    const names = new Set<string>();
    list.forEach((raw: unknown, index) => {
      const entry = isPlainObject(raw) ? raw : {};
      const problem = problemOf(entry);
      if (problem) problems.push(`${at}.library.${key}[${index}]: ${problem}`);
      const name = typeof entry.name === 'string' ? entry.name.trim() : '';
      if (name && names.has(name)) problems.push(`${at}.library.${key}[${index}]: ${name} is listed twice.`);
      names.add(name);
      if (key === 'mcpServers' && entry.secrets !== undefined) {
        const secrets = Array.isArray(entry.secrets) ? entry.secrets : [null];
        for (const secret of secrets) {
          if (typeof secret !== 'string' || !ORG_SECRET_NAME.test(secret)) problems.push(`${at}.library.${key}[${index}].secrets: ${String(secret)} is not an organization secret’s name.`);
          else if (isIntegrationSecret(secret)) problems.push(`${at}.library.${key}[${index}].secrets: ${secret} is a tracker’s sign-in, which never reaches a session.`);
        }
      }
    });
  }
  return orgExtensionsOf(input);
}

/** A library entry as a workspace's list holds it, switched on — what Add puts there. */
export function fromLibrary(entry: LibraryMcpServer): McpServerEntry;
export function fromLibrary(entry: LibrarySkill): SkillEntry;
export function fromLibrary(entry: LibraryPlugin): PluginEntry;
export function fromLibrary(entry: LibraryMcpServer | LibrarySkill | LibraryPlugin): McpServerEntry | SkillEntry | PluginEntry {
  if ('command' in entry) {
    const { secrets: _secrets, ...server } = entry;
    return { ...server, enabled: true };
  }
  return { ...entry, enabled: true };
}

/**
 * Whether an entry a workspace switched on is the library's: the same name and
 * the same everything that decides what runs — a library server's name over
 * another command is not the library's server.
 */
export function inLibrary(library: OrgExtensions['library'], kind: 'mcpServers' | 'skills' | 'plugins', entry: { name: string }): boolean {
  const { enabled: _on, ...wanted } = entry as { name: string; enabled?: boolean };
  return (library[kind] as { name: string; secrets?: string[] }[]).some(({ secrets: _secrets, ...one }) => sameValue(one, wanted));
}

/** The member's organizations that hold an allowlist, each with its library — what a session start is checked against. */
export interface MemberExtensions {
  allowlists: { org: string; library: OrgExtensions['library'] }[];
}

export function memberExtensions(orgs: readonly { name: string; extensions: OrgExtensions }[]): MemberExtensions {
  return { allowlists: orgs.filter((one) => one.extensions.allowlist).map((one) => ({ org: one.name, library: one.extensions.library })) };
}

/** Whether any organization narrows what a session may be given. */
export function extensionsDecide(member: MemberExtensions | null | undefined): boolean {
  return !!member && member.allowlists.length > 0;
}

const KIND_WORD = { mcpServers: 'MCP server', skills: 'skill', plugins: 'plugin' } as const;

/**
 * Why a session given `extensions` may not start, or null: the first entry
 * switched on that an allowlisting organization's library does not hold, named
 * with that organization. Every allowlist holds — two organizations' rules are
 * two promises, and one may not lift the other's.
 */
export function extensionRefusal(
  member: MemberExtensions | null | undefined,
  extensions: AgentExtensions,
  /**
   * Where the agent's own configuration comes from (`agentConfigSource`):
   * `host` copies this machine's, whose MCP servers and plugins no library
   * vouches for — so under an allowlist it is refused too, rather than letting
   * them in by the side door.
   */
  configSource?: 'host' | 'none'
): string | null {
  if (!extensionsDecide(member)) return null;
  if (configSource === 'host') {
    return `${member!.allowlists[0].org} allows only the MCP servers, skills and plugins in its library, and this workspace copies your own agent configuration, whose servers and plugins it does not cover. Set “Reuse the agent's config from this computer” to “Start from nothing” in this workspace's Agent settings, then start again.`;
  }
  for (const kind of ['mcpServers', 'skills', 'plugins'] as const) {
    for (const entry of extensions[kind] as { name: string; enabled: boolean }[]) {
      if (!entry.enabled) continue;
      const refusing = member!.allowlists.find((one) => !inLibrary(one.library, kind, entry));
      if (refusing) {
        return `${refusing.org} allows only the MCP servers, skills and plugins in its library — the ${KIND_WORD[kind]} “${entry.name}” is not in it. Switch it off for this workspace, or ask an administrator to add it to the library.`;
      }
    }
  }
  return null;
}
